
import {
  Autocomplete,
  Box,
  InputAdornment,
  TextField,
  Typography
} from '@mui/material';
import { isEqual } from 'lodash';
import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import 'simplebar-react/dist/simplebar.min.css';
import { ImageIcon } from 'utils/UtilsComponent';
import ArrowDownIcon from '../../assets/icons/arrow-down-icon.svg';

export interface SelectType {
  value: any;
  label: string;
  [key: string]: any;
}

export interface Props {
  data?: SelectType[];
  selected?: any;
  setSelected?: (selectedId: string, selectedItem?: SelectType) => void;
  label?: string;
  style?: CSSProperties;
  disabled?: boolean;
  placeholder?: string;
  maxHeight?: any;
  fullWidth?: boolean;
  error?: any;
  topLable?: string;
  noMarginTop?: boolean;
  errorEmpty?: boolean;
  renderItem?: (item: SelectType, isActive: boolean) => React.ReactNode;
  [key: string]: any;
}

const Select: React.FC<Props> = ({
  data = [],
  selected,
  setSelected,
  style = {},
  disabled,
  hideSelected = true,
  maxHeight = 324,
  fullWidth,
  placeholder,
  error,
  topLable,
  noMarginTop,
  errorEmpty,
  renderItem,
  ...rest
}) => {
  const anchorRef = useRef<HTMLInputElement>(null);
  const touched = useRef(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (errorEmpty) touched.current = true;
  }, [errorEmpty]);

  const readOnly = true;

  let newData = data;
  const value = data.find((item) => isEqual(item.value, selected))?.label || '';

  // Open the menu
  const handleOpen = () => {
    setOpen(true);
  };
  const handleToggle = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setOpen((prev) => !prev);
  };

  // Close the menu
  const handleClose = (hasValue?: boolean) => {
    touched.current = true;
    setOpen(false);
  };

  // When click item, set selected item, set value in textbox and close menu
  const handleClickItem = (e: any, item?: SelectType) => {
    if (item) {
      setSelected?.(item.value || '', item);
    }
    handleClose(true);
  };

  const errorText = !open && touched.current ? error : undefined;

  const errorTextField = errorText;

  return (
    <Box
      sx={{
        cursor: 'pointer !important',
        width: fullWidth ? '100%' : undefined,
        ...style,
      }}
    >
      {topLable && (
        <Typography
          style={{
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '20px',
            padding: 0,
            marginTop: noMarginTop ? undefined : 20,
            marginBottom: 8,
            cursor: 'pointer !important',
          }}
        >
          {topLable}
        </Typography>
      )}
      <Box sx={{ background: '#fff', borderRadius: '8px' }}>
        <Autocomplete
          id="free-solo-demo"
          freeSolo
          disableClearable
          clearOnEscape={false}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          options={newData}
          onChange={(event, newValue) => handleClickItem(event, (newValue as SelectType) || undefined)}
          value={value}
          onInputChange={(e, val, reason) => {
            if (reason === 'input' && val === '') {
              const allItem = data.find((x) => x.value === 'all');
              setSelected?.('all', allItem);
            }
          }}
          ListboxProps={{
            sx: {
              '& .MuiAutocomplete-option': {
                borderBottom: '1px solid #e0e0e0',
                padding: '10px 16px',
                fontSize: '14px',

                '&:last-child': {
                  borderBottom: 'none',
                },
              },
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={placeholder}
              helperText={errorTextField}
              error={!!errorTextField}
              disabled={disabled}
              InputProps={{
                ...params.InputProps,
                readOnly,
                endAdornment: (
                  <>
                    {params.InputProps.endAdornment}
                    <InputAdornment
                      position="end"
                      onClick={handleToggle}
                      sx={{
                        cursor: disabled ? 'default' : 'pointer',
                        transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: '0.2s',
                      }}
                    >
                      <ImageIcon image={ArrowDownIcon} />
                    </InputAdornment>
                  </>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  border: '1px solid #d9d9d9 !important',
                },
                '& .MuiInputBase-root': {
                  borderRadius: '8px',
                  height: '44px',
                  color: '#1E2323',
                  fontWeight: '500',
                  fontSize: '14px',
                  minWidth: '200px',

                  display: 'flex',
                  alignItems: 'center',
                  padding: '10px !important',
                },

                input: {
                  height: '100%',
                  padding: '0 14px !important',
                  lineHeight: '44px',
                  background: '#fff',
                  cursor: `${disabled ? 'default' : 'pointer'} !important`,
                  '&::placeholder': {
                    color: '#777777',
                  },
                },
              }}
            />
          )}
        />
      </Box>
    </Box>
  );
};

export default Select;
